import type { CodeableConcept, Period } from "./common";
import type { ConditionId } from "./condition.model";
import { InvalidTransitionError, PreconditionFailedError } from "./errors";
import type { DomainId } from "./lib/domain-id";
import type { PatientId } from "./patient.model";
import { ACQUISITION_SOURCES, type CaseAcquisition } from "./patient-matching";
import type { PractitionerId } from "./practitioner.model";

/** Stable Case domain identity, independent of the FHIR EpisodeOfCare locator. */
export type CaseId = DomainId<"CaseId">;

/** Boundary for identities restored from persistence or accepted by contracts. */
export function asCaseId(value: string): CaseId {
  if (!value) throw new PreconditionFailedError("A Case identity cannot be empty");
  return value as CaseId;
}

// ============================================================
// Case — one episode of a patient's care (ADR-0017)
//
// FHIR stores it as an EpisodeOfCare. It is the container everything clinical
// hangs off: the working problem list for this episode and the care manager.
//
// Status describes whether the episode is open. Clinical progress is derived
// from committed chart state. New cases open active alongside registration.
// ============================================================

export type CaseStatus = "active" | "finished" | "cancelled" | "entered-in-error";

type CaseEvent = "FINISH" | "CANCEL";

const TRANSITIONS: Record<CaseStatus, Partial<Record<CaseEvent, CaseStatus>>> = {
  active: { FINISH: "finished", CANCEL: "cancelled" },
  finished: {},
  cancelled: {},
  "entered-in-error": {},
};

/** The episode is over, one way or another. Nothing may be added to it. */
const CLOSED: ReadonlySet<CaseStatus> = new Set(["finished", "cancelled", "entered-in-error"]);

/** One entry on the episode's working problem list. */
interface Diagnosis {
  conditionId: ConditionId;
  /** Read alongside the condition so the case can refuse a duplicate itself. */
  code?: CodeableConcept;
}

export interface CaseProps {
  acquisition?: CaseAcquisition;
  id: CaseId;
  status: CaseStatus;
  patientId: PatientId;
  type?: CodeableConcept[];
  period?: Period;
  careManagerId?: PractitionerId;
  /** Additional identifiers carried by the episode. */
  identifier?: Array<{ system?: string; value?: string }>;
  /** The working problem list for this episode. */
  diagnoses: Diagnosis[];
}

export type CreateCaseInput = Omit<CaseProps, "status">;

/**
 * What persistence can actually hand back. A stored episode's patient reference
 * may have lost its domain identity — Medplum rewrites a conditional reference
 * to a literal and drops the sibling identifier — and the included Patient row
 * is then the only source left. When neither carries it the row is unusable,
 * which the constructor already says.
 */
export type StoredCaseProps = Omit<CaseProps, "patientId"> & { patientId?: PatientId };

export class Case {
  private state: CaseProps;

  private constructor(props: CaseProps) {
    if (!props.patientId) {
      throw new PreconditionFailedError("A case requires a patient");
    }
    this.state = structuredClone(props);
  }

  static create(input: CreateCaseInput): Case {
    if (input.acquisition && !ACQUISITION_SOURCES.includes(input.acquisition.source)) {
      throw new PreconditionFailedError("Unknown acquisition source");
    }

    return new Case({ ...structuredClone(input), status: "active", id: asCaseId(input.id) });
  }

  /**
   * A registration starts a new episode only when the patient is not already in
   * one: a returning patient joins their active case, and a retried registration
   * finds the case it opened the first time.
   */
  static openForRegistration(
    input: { id: string; patientId: PatientId; acquisition?: CaseAcquisition },
    existing: readonly Case[],
    now: Date,
  ): Case | undefined {
    if (existing.some((kase) => kase.isActive || kase.id === input.id)) return undefined;
    return Case.create({
      id: asCaseId(input.id),
      patientId: input.patientId,
      period: { start: now.toISOString() },
      diagnoses: [],
      acquisition: input.acquisition,
    });
  }

  static restore(snapshot: StoredCaseProps): Case {
    return new Case(snapshot as CaseProps);
  }

  // ── facts ──

  get id(): CaseId {
    return this.state.id;
  }

  get status(): CaseStatus {
    return this.state.status;
  }

  get patientId(): PatientId {
    return this.state.patientId;
  }

  /** When the episode ran. Read by `selectCurrentCase` to order a patient's history. */
  get period(): Readonly<Period> | undefined {
    return this.state.period;
  }

  /** Still running. What a caller folds over a patient's history to find the current one. */
  get isOpen(): boolean {
    return !CLOSED.has(this.state.status);
  }

  /** Whether the episode is active. */
  get isActive(): boolean {
    return this.state.status === "active";
  }

  get careManagerId(): PractitionerId | undefined {
    return this.state.careManagerId;
  }

  /** Condition ids on this episode's problem list. */
  get diagnosisIds(): readonly ConditionId[] {
    return this.state.diagnoses.map((d) => d.conditionId);
  }

  snapshot(): CaseProps {
    return structuredClone(this.state);
  }

  // ── lifecycle ──

  finish(): void {
    this.state.status = this.next("FINISH");
  }

  cancel(): void {
    this.state.status = this.next("CANCEL");
  }

  // ── the episode's content ──

  /** The provider bookings default to. Refused once the episode is closed. */
  assignCareManager(practitionerId: PractitionerId): void {
    this.assertOpen("assign a care manager to");
    if (!practitionerId) {
      throw new PreconditionFailedError("A care manager is required");
    }
    this.state.careManagerId = practitionerId;
  }

  /**
   * Chart a diagnosis on this episode. Refuses a duplicate by CODE, not by id —
   * the same diagnosis recorded twice as two Conditions is still one problem,
   * and a problem list that lists it twice misleads whoever reads it.
   */
  chartDiagnosis(conditionId: ConditionId, code: CodeableConcept): void {
    this.assertOpen("add a diagnosis to");
    // Re-charting the SAME Condition is a no-op, not a conflict — a retry must
    // not read as "you already recorded this".
    if (this.diagnosisIds.includes(conditionId)) return;
    const key = codeKey(code);
    const duplicate = this.state.diagnoses.find((d) => d.code && codeKey(d.code) === key);
    if (duplicate) {
      throw new PreconditionFailedError(`This diagnosis is already on the case (Condition '${duplicate.conditionId}')`);
    }
    this.state.diagnoses.push({ conditionId, code });
  }

  // ── internals ──

  private next(event: CaseEvent): CaseStatus {
    const target = TRANSITIONS[this.state.status]?.[event];
    if (!target) throw new InvalidTransitionError(this.state.status, `Case:${event}`);
    return target;
  }

  private assertOpen(action: string): void {
    if (!this.isOpen) {
      throw new PreconditionFailedError(`Cannot ${action} a case in '${this.state.status}' status`);
    }
  }
}

/** Two codes are the same diagnosis when their primary coding matches, or their text does. */
function codeKey(code: CodeableConcept): string {
  const first = code.coding?.[0];
  return first?.system && first.code ? `${first.system}|${first.code}` : `text:${code.text ?? ""}`;
}
